import React, { useState } from 'react';
import { X } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import LoadingDots from './LoadingDots';

const CANCEL_REASONS = [
  'Too expensive',
  'Not finding leads',
  'Found alternative',
  'Not using enough',
  'Missing features',
  'Technical issues',
];

export default function CancelModal({ isOpen, onClose, currentPlan, onCancelled }) {
  const [step, setStep] = useState(0);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [feedback, setFeedback] = useState('');
  const [cancelling, setCancelling] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setStep(0);
      setSelectedReasons([]);
      setFeedback('');
      setCancelling(false);
    }
  }, [isOpen]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.post('/api/billing/cancel', {
        reason: selectedReasons.join(', ') || feedback || 'Not specified',
        feedback,
        selected_reasons: selectedReasons,
      });
      onClose();
      toast.success('Subscription cancelled. You will retain access until the end of your billing period.');
      if (onCancelled) onCancelled();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription.');
    } finally {
      setCancelling(false);
    }
  };

  const toggleReason = (reason) => {
    setSelectedReasons((prev) =>
      prev.includes(reason) ? prev.filter((r) => r !== reason) : [...prev, reason]
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={() => { onClose(); setStep(0); }}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480, borderRadius: 20, padding: 'var(--space-2xl)' }}>
        <button
          className="modal-close"
          onClick={() => { onClose(); setStep(0); }}
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {step === 0 ? (
          <>
            <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
              <span style={{ fontSize: 40 }}>😢</span>
              <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '8px 0' }}>
                Cancel Your Subscription?
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                We'd hate to see you go.
              </p>
            </div>

            <p style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 8, fontWeight: 600 }}>
              What's making you leave?
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 'var(--space-lg)' }}>
              {CANCEL_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => toggleReason(reason)}
                  className={`cancel-reason-btn ${selectedReasons.includes(reason) ? 'selected' : ''}`}
                >
                  {reason}
                </button>
              ))}
            </div>

            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Any other feedback?"
              rows={2}
              style={{
                width: '100%', padding: 10, borderRadius: 8,
                border: '1px solid var(--border-primary)',
                background: 'var(--input-bg)', color: 'var(--text-primary)',
                fontSize: 12, outline: 'none', resize: 'vertical',
                fontFamily: 'inherit', marginBottom: 'var(--space-lg)',
                boxSizing: 'border-box',
              }}
            />

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: 12, borderRadius: 10,
                  border: '1px solid var(--border-primary)',
                  background: 'transparent', color: 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                Keep My Plan
              </button>
              <button
                onClick={() => setStep(1)}
                style={{
                  flex: 1, padding: 12, borderRadius: 10,
                  border: 'none', background: 'var(--error)',
                  color: 'white', cursor: 'pointer', fontSize: 13,
                  fontWeight: 700, fontFamily: 'inherit',
                }}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
              <span style={{ fontSize: 56 }}>💔</span>
              <h3 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '8px 0' }}>
                Last Chance
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                Need help? Reach out to{' '}
                <strong style={{ color: 'var(--accent-primary)' }}>support@ileadx.io</strong>
              </p>
            </div>

            <div style={{
              padding: 14, background: 'var(--error-bg)', borderRadius: 10,
              border: '1px solid var(--error)', marginBottom: 'var(--space-lg)',
            }}>
              <p style={{ color: 'var(--error)', fontSize: 12, fontWeight: 600, margin: 0 }}>
                ⚠️ You'll lose extra searches, AI scoring, and priority support after your billing period ends. Your account will become inactive.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setStep(0)}
                style={{
                  flex: 1, padding: 12, borderRadius: 10,
                  border: '1px solid var(--success)',
                  background: 'var(--success-bg)', color: 'var(--success)',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  fontFamily: 'inherit',
                }}
              >
                I'll Stay
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                style={{
                  flex: 1, padding: 12, borderRadius: 10,
                  border: 'none', background: 'var(--error)',
                  color: 'white', cursor: cancelling ? 'default' : 'pointer',
                  fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
                  opacity: cancelling ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                {cancelling ? (
                  <LoadingDots color="white" />
                ) : (
                  'Cancel My Subscription'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
